import { useEffect, useMemo, useState } from 'react';
import type { Tool } from '@orqforge/shared';
import { deriveToolGroups } from '@orqforge/shared';
import { apiClient } from '../lib/api-client';
import { useApp } from '../contexts/AppContext';
import { Badge } from '../components/ui/Badge';

function summarizeConfig(tool: Tool): string {
  const cfg = tool.config as Record<string, unknown>;
  const handler = cfg['handler'] as string | undefined;
  const remoteName = cfg['remoteToolName'] as string | undefined;
  if (handler === 'coding') {
    return remoteName ? `Coding → ${remoteName}` : 'Coding agent (parent)';
  }
  return `handler: ${handler ?? tool.kind}`;
}

export function ToolsPage() {
  const { workspaceId } = useApp();
  const [tools, setTools] = useState<Tool[]>([]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set(['coding']));

  useEffect(() => {
    void apiClient
      .listTools('', 200)
      .then((result) => setTools(result.items.filter((t) => t.kind === 'builtin')))
      .catch(() => setTools([]));
  }, [workspaceId]);

  const groups = useMemo(() => deriveToolGroups(tools), [tools]);

  function toggle(key: string) {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  return (
    <div>
      <h1 className="text-2xl font-semibold">Tool Catalog</h1>
      <p className="text-sm text-gray-500">
        Platform capabilities available to agents. For external APIs use <strong>Integrations</strong> or register an <strong>MCP server</strong>. Its tools auto-discover when granted to an agent.
      </p>

      {groups.length === 0 ? (
        <div className="mt-4 text-center text-sm text-gray-500">No built-in tools available.</div>
      ) : (
        groups.map((group) => {
          const open = expanded.has(group.key);
          return (
            <div key={group.key} className="mt-4 overflow-x-auto">
              <button
                type="button"
                className="flex w-full items-center gap-3 rounded-xl border border-gray-200 px-3 py-2.5 text-left hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800/50"
                onClick={() => toggle(group.key)}
              >
                <span className="text-[10px] text-gray-400">{open ? '▼' : '▶'}</span>
                <span className="flex-1 text-sm font-medium">{group.label}</span>
                <Badge variant="secondary">{group.tools.length}</Badge>
                {group.description && (
                  <span className="hidden text-xs text-gray-500 sm:inline">{group.description}</span>
                )}
              </button>
              {open && (
                <table className="mt-1 w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-gray-500">
                      <th className="w-48 py-2">Key</th>
                      <th>Name</th>
                      <th>What it does</th>
                      <th className="w-20">Approval</th>
                      <th className="w-20">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.tools.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="py-3 text-center text-sm text-gray-500">No tools in this group.</td>
                      </tr>
                    ) : (
                      group.tools.map((tool) => (
                        <tr key={tool.key} className="border-t border-gray-100 dark:border-gray-800">
                          <td className="py-2"><code className="text-xs">{tool.key}</code></td>
                          <td>
                            <div className="font-medium">{tool.name}</div>
                            {tool.description && <div className="text-xs text-gray-500">{tool.description}</div>}
                          </td>
                          <td className="max-w-xs truncate text-xs text-gray-500">{summarizeConfig(tool)}</td>
                          <td>
                            {tool.requiresApproval ? <Badge variant="warning">required</Badge> : <span className="text-xs text-gray-400">-</span>}
                          </td>
                          <td>
                            <Badge variant={tool.enabled ? 'success' : 'destructive'}>
                              {tool.enabled ? 'enabled' : 'disabled'}
                            </Badge>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}
